import {FormikErrors} from 'formik';
import set from 'lodash/set';
import get from 'lodash/get';
import moment from 'moment';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {
  validateAmount,
  validateTextField,
} from '../CreateRecommend/RecommendSourceDestination/validators';
import FREQUENCY, {FREQUENCY_COMBOS} from '../../../constants/Frequency';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {BankDetailsEnum} from '../../../schema-types/globalTypes';
import {SaltusAny} from '../../../utils/SaltusAny';

export const validateWithdrawalFrequency = (
  values: recommendFieldsType,
  errors: FormikErrors<recommendFieldsType>
) => {
  const frequency = values.withdraw?.source?.frequency;
  const start = values.withdraw?.source?.start;

  if (!frequency) {
    set(errors, 'withdraw.source.frequency', 'Frequency is required');
    return;
  }

  if (frequency !== FREQUENCY.SINGLE) {
    if (!start) {
      set(errors, 'withdraw.source.start', 'Start date is required');
      return;
    }
    const minDate = moment.utc().add(10, 'days');
    if (minDate.isAfter(start)) {
      set(errors, 'withdraw.source.start', 'Date must be at least 10 days in the future');
    }
  }
};

export const withdrawalValidator = (
  values: recommendFieldsType,
  errors: SaltusAny
): FormikErrors<recommendFieldsType> => {
  const withdrawFrequency = values.withdraw?.source?.frequency;
  const hasSingle = withdrawFrequency === FREQUENCY.SINGLE;
  const hasRecurring = !!withdrawFrequency && withdrawFrequency !== FREQUENCY.SINGLE;
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const withdrawValues = values.withdraw;
  if (!withdrawValues?.source) {
    errors.withdraw = 'Select withdrawal source';
    return errors;
  }
  if (!withdrawValues.source?.id) {
    set(errors, 'withdraw.source', 'Required');
  }
  const withdrawReason = get(values, 'withdraw.source.reason');
  validateTextField(withdrawReason, 'withdraw.source.reason', errors);

  if (hasSingle && withdrawReason !== 'Max Tax Free Cash') {
    validateAmount({
      sourceValues: withdrawValues.source,
      errors,
      amountType: 'singleClientAmount',
      key: 'withdraw.source.singleClientAmount',
    });
  }
  if (hasRecurring) {
    validateAmount({
      sourceValues: withdrawValues.source,
      amountType: 'recurringClientAmount',
      errors,
      key: 'withdraw.source.recurringClientAmount',
    });
  }
  validateWithdrawalFrequency(values, errors);
  if (values.withdraw?.destination?.id === NEW_PLAN_ID) {
    NewCurrentAccountValidator(values, errors);
  }
  return errors;
};

export const validateBankAccount = (account: string) =>
  /^(?!(?:0{8}))(\d){8}$/.test(account);
export const validateSortCode = (sortCodeWithDashes: string) => {
  const sortCode = sortCodeWithDashes.replaceAll('-', '');
  return /^(?!(?:0{6}|000000))(?:\d{6}|\d\d\d\d\d\d)$/.test(sortCode);
};

export const NewCurrentAccountValidator = (
  values: recommendFieldsType,
  errors: FormikErrors<recommendFieldsType>
): FormikErrors<recommendFieldsType> => {
  const detailsVal = get(values, 'withdraw.destination.bankDetails');
  if (!detailsVal) {
    set(errors, 'withdraw.destination.bankDetails', 'Required');
  } else if (detailsVal === BankDetailsEnum.NOT_KNOWN) {
    // no need to do any checks then
    const accountNameVal = get(values, 'withdraw.destination.accountName');
    if (!accountNameVal) {
      set(errors, 'withdraw.destination.accountName', 'Required');
    }
    return errors;
  }

  const sortCodeVal = get(values, 'withdraw.destination.sortCode');
  if (!sortCodeVal) {
    set(errors, 'withdraw.destination.sortCode', 'Required');
  } else if (!validateSortCode(sortCodeVal)) {
    set(errors, 'withdraw.destination.sortCode', 'Invalid sort code: must be 6 digits');
  }

  const accountNumberVal = get(values, 'withdraw.destination.accountNumber');
  const ownershipVal = get(values, 'withdraw.destination.contactId');
  if (!ownershipVal) {
    set(errors, 'withdraw.destination.contactId', 'Required');
  }

  const bankNameVal = get(values, 'withdraw.destination.productProvider');
  if (!bankNameVal) {
    set(errors, 'withdraw.destination.productProvider', 'Required');
  }

  const accountNameVal = get(values, 'withdraw.destination.accountName');
  if (!accountNameVal) {
    set(errors, 'withdraw.destination.accountName', 'Required');
  }

  if (!accountNumberVal) {
    set(errors, 'withdraw.destination.accountNumber', 'Required');
  } else if (!validateBankAccount(accountNumberVal)) {
    set(
      errors,
      'withdraw.destination.accountNumber',
      'Invalid account number: must be 8 digits'
    );
  }

  return errors;
};
